import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, Variants, Transition } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import '../assets/pageTransition.css';

interface PageTransitionProps {
  children: React.ReactNode;
}

const PageTransition: React.FC<PageTransitionProps> = ({ children }) => {
  const location = useLocation();
  const [isDesktop, setIsDesktop] = useState<boolean>(window.innerWidth >= 768);
  
  // Update on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Desktop variant with minimal animation
  const desktopVariants: Variants = {
    initial: { opacity: 0.97, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0.97, y: -10 }
  };
  
  // Mobile variant with more pronounced animation
  const mobileVariants: Variants = {
    initial: { opacity: 0.9, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0.9, y: -20 }
  };
  
  // Transition settings based on device
  const desktopTransition: Transition = {
    duration: 0.15,
    ease: [0.4, 0, 0.2, 1] // easeInOut using cubic-bezier values
  };
  
  const mobileTransition: Transition = {
    duration: 0.2,
    ease: [0.4, 0, 0.2, 1] // easeInOut using cubic-bezier values
  };
  
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={isDesktop ? desktopVariants : mobileVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={isDesktop ? desktopTransition : mobileTransition}
        className={`page-transition-container ${isDesktop ? 'desktop-transition' : 'mobile-transition'}`}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

export default PageTransition;
